import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FileText, Users, Gavel, Search, Sparkles, FileBarChart, Clock, Shield, Zap, ArrowRight, CheckCircle2, User, Settings, LogOut, ChevronDown, Play, Cpu, Code, Building2, X, Target, Download, Flame, BellRing, Bookmark, Loader } from 'lucide-react';
import { userTierService } from '../lib/userTierService';
import { HeroSummaryDemo } from './HomePage.HeroSummaryDemo';
import { useDocketAutocomplete } from '../hooks/useDocketAutocomplete';
import { LANDING_COPY, inferAudienceFromEmail, type Audience } from '../content/landingCopy';

interface HomePageProps {
  isAnonymous?: boolean;
  onSignIn?: () => void;
  userEmail?: string | null;
  onSignOut?: () => void;
  /** Which audience voice to render. "/" is neutral; "/business" passes "business". */
  audience?: Audience;
}

// Rotating type/delete hero phrase. Cycles through the product's main
// use-cases so the H1 communicates breadth without needing four headlines.
// Honors `prefers-reduced-motion`: snaps directly to each phrase instead
// of typing for users who've opted out of animation.
const HERO_PHRASES = [
  'comment summaries, page-cited.',
  'filings, downloaded in one click.',
  'watchlist alerts on every important docket.',
  'OPIF files, downloaded without the crashes.',
] as const;

const HeroTypewriter: React.FC<{ className?: string }> = ({ className = '' }) => {
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [text, setText] = useState(HERO_PHRASES[0]);
  const [phase, setPhase] = useState<'hold' | 'deleting' | 'typing'>('hold');
  const reducedMotion = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    reducedMotion.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  useEffect(() => {
    const current = HERO_PHRASES[phraseIdx];

    if (reducedMotion.current) {
      // No animation — just rotate the full phrase on a long timer.
      setText(current);
      const t = setTimeout(() => setPhraseIdx((i) => (i + 1) % HERO_PHRASES.length), 3500);
      return () => clearTimeout(t);
    }

    let timer: ReturnType<typeof setTimeout>;
    if (phase === 'hold') {
      setText(current);
      timer = setTimeout(() => setPhase('deleting'), 2200);
    } else if (phase === 'deleting') {
      if (text.length === 0) {
        setPhraseIdx((i) => (i + 1) % HERO_PHRASES.length);
        setPhase('typing');
      } else {
        timer = setTimeout(() => setText(text.slice(0, -1)), 22);
      }
    } else {
      // typing
      if (text.length === current.length) {
        setPhase('hold');
      } else {
        timer = setTimeout(() => setText(current.slice(0, text.length + 1)), 45);
      }
    }
    return () => clearTimeout(timer);
  }, [phase, text, phraseIdx]);

  return (
    <span className={className}>
      {text}
      <span
        aria-hidden
        className="ml-0.5 inline-block w-[2px] animate-pulse self-end bg-current align-baseline"
        style={{ height: '0.9em' }}
      />
    </span>
  );
};

// FAQ Item Component
const FAQItem: React.FC<{ question: string; answer: React.ReactNode; defaultOpen?: boolean }> = ({ question, answer, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="overflow-hidden rounded-xl border border-cg-border bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between px-6 py-4 text-left transition-colors hover:bg-cg-cream/50"
        aria-expanded={isOpen}
      >
        <span className="text-lg font-medium text-cg-ink">{question}</span>
        <ChevronDown className={`w-5 h-5 text-gray-600 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="px-6 pb-5">
          <div className="text-gray-700 leading-relaxed space-y-3">{answer}</div>
        </div>
      )}
    </div>
  );
};

export const HomePage: React.FC<HomePageProps> = ({ isAnonymous = false, onSignIn, userEmail, onSignOut, audience = 'neutral' }) => {
  // HomePage component rendering
  // Public landing uses the passed audience voice; the logged-in dashboard adapts
  // from the signup email domain (firm → attorney, general → business).
  const copy = LANDING_COPY[audience];
  const dashboardCopy = LANDING_COPY[inferAudienceFromEmail(userEmail)];
  const navigate = useNavigate();
  const [userTier, setUserTier] = useState<string>('free');
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);
  const accountDropdownRef = useRef<HTMLDivElement>(null);
  const [activeWorkflow, setActiveWorkflow] = useState<'commentPeriods' | 'watchlist'>('commentPeriods');
  const [showWelcomeBanner, setShowWelcomeBanner] = useState<boolean>(() => {
    // Check if user has dismissed the banner before
    if (isAnonymous) return false;
    const dismissed = localStorage.getItem('welcomeBannerDismissed');
    return dismissed !== 'true';
  });
  const [docketSearchInput, setDocketSearchInput] = useState('');
  const docketSearchContainerRef = useRef<HTMLDivElement>(null);
  const {
    suggestions: docketSearchSuggestions,
    isLoading: docketSearchLoading,
    showSuggestions: docketSearchShowSuggestions,
    selectedIndex: docketSearchSelectedIndex,
    setQuery: docketSearchSetQuery,
    selectSuggestion: docketSearchSelectSuggestion,
    handleKeyDown: docketSearchHandleKeyDown,
    setShowSuggestions: docketSearchSetShowSuggestions,
  } = useDocketAutocomplete({ minQueryLength: 2, maxSuggestions: 8 });

  useEffect(() => {
    const onClick = (ev: MouseEvent) => {
      if (
        docketSearchContainerRef.current &&
        !docketSearchContainerRef.current.contains(ev.target as Node)
      ) {
        docketSearchSetShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [docketSearchSetShowSuggestions]);

  // Fetch user tier when component mounts (only if not anonymous)
  useEffect(() => {
    if (!isAnonymous) {
      const fetchUserTier = async () => {
        try {
          const tier = await userTierService.getUserTier();
          setUserTier(tier);
        } catch (error) {
          console.error('Error fetching user tier:', error);
          setUserTier('free');
        }
      };

      fetchUserTier();
    }
  }, [isAnonymous]);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (accountDropdownRef.current && !accountDropdownRef.current.contains(event.target as Node)) {
        setAccountDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleAccountDropdown = () => {
    setAccountDropdownOpen(!accountDropdownOpen);
  };

  const dismissWelcomeBanner = () => {
    setShowWelcomeBanner(false);
    localStorage.setItem('welcomeBannerDismissed', 'true');
  };

  // Format tier name for display
  const getTierDisplayName = (tier: string): string => {
    switch (tier) {
      case 'free':
        return 'Free Plan';
      case 'pro':
        return 'Pro Plan';
      case 'enterprise':
        return 'Enterprise Plan';
      case 'anonymous':
        return 'Anonymous';
      default:
        return 'Free Plan';
    }
  };

  const menuOptions = [
    {
      id: 'proceedings',
      title: 'Find a proceeding or docket',
      description: 'Browse by docket number, topic, or keyword. Jump straight into the record.',
      icon: Gavel,
      path: '/proceedings',
      iconColor: 'text-blue-600',
      accent: 'green' as const,
    },
    {
      id: 'trending',
      title: "See what's trending",
      description: 'Dockets with the most recent filing activity, surfaced automatically.',
      icon: Flame,
      path: '/trending',
      iconColor: 'text-orange-600',
      accent: 'amber' as const,
    },
  ];

  const bottomMenuOptions = [
    {
      id: 'summaries',
      title: 'My Summaries',
      description: 'Access your saved docket summaries',
      icon: FileText,
      path: '/library?tab=summaries',
      iconColor: 'text-emerald-700'
    },
    {
      id: 'workspaces',
      title: 'Workspaces',
      description: 'Share summaries and analyses with your team',
      icon: Users,
      path: '/workspaces',
      iconColor: 'text-teal-700'
    }
  ];

  // Landing page for anonymous users
  if (isAnonymous) {
    return (
      <div className="w-full">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-cg-cream px-4 py-16 sm:px-6 sm:py-24">
          {/* Soft radial highlight behind hero — subtle visual depth */}
          <div
            aria-hidden
            className="pointer-events-none absolute -top-32 left-1/2 h-[520px] w-[680px] -translate-x-1/2 rounded-full bg-cg-green/10 blur-3xl"
          />
          <div className="relative mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-[1.05fr_1fr] lg:gap-16">
            {/* Left: copy + CTAs */}
            <div className="text-center lg:text-left">
              <p className="mb-4 text-[11px] font-medium uppercase tracking-[0.1em] text-cg-green">
                FCC Regulatory Intelligence
              </p>
              <h1 className="mb-6 min-h-[7.5rem] font-display text-4xl font-normal leading-[1.05] tracking-tight text-cg-ink sm:min-h-[9rem] sm:text-5xl lg:min-h-[10.5rem] lg:text-[3.5rem]">
                FCC <HeroTypewriter className="italic text-cg-green" />
              </h1>
              <p className="mx-auto mb-9 max-w-2xl text-lg font-light leading-relaxed text-neutral-600 sm:text-xl lg:mx-0">
                {copy.heroSubhead}
              </p>
              <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center lg:justify-start">
                <button
                  type="button"
                  onClick={() => onSignIn?.()}
                  className="group flex w-full items-center justify-center gap-2 rounded-xl bg-cg-nav px-8 py-4 text-lg font-medium text-white shadow-md transition-all duration-200 hover:bg-cg-green hover:shadow-lg sm:w-auto"
                >
                  <span>Sign Up Free</span>
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5" />
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/proceedings')}
                  className="w-full rounded-xl border border-cg-border bg-white px-8 py-4 text-lg font-medium text-cg-ink shadow-sm transition-all duration-200 hover:border-neutral-400 hover:bg-neutral-50 sm:w-auto"
                >
                  Browse dockets — no signup
                </button>
              </div>
              <p className="mt-4 text-sm font-light text-neutral-500 lg:text-left">
                5 AI summaries/month free, no credit card required
              </p>
            </div>

            {/* Right: live cycling demo — loading screen → output reveal → loop. */}
            <HeroSummaryDemo />
          </div>
        </section>

        {/* Stat band — three confident claims with real visual weight. Replaces the
            previous thin credibility strip; intent is "this registers" not "this divides
            the page." Verify numbers stay defensible. */}
        <section className="border-y border-cg-border bg-white px-4 py-14 sm:px-6 sm:py-16">
          <div className="mx-auto grid max-w-6xl grid-cols-1 gap-y-10 sm:grid-cols-3 sm:gap-x-10 text-center">
            <div>
              <div className="font-display text-4xl font-normal text-cg-green sm:text-[2.75rem]">
                2–3 days → 3 min
              </div>
              <div className="mt-2 text-sm font-light text-neutral-600">
                {copy.statBandCaption}
              </div>
            </div>
            <div>
              <div className="font-display text-4xl font-normal text-cg-ink sm:text-[2.75rem]">
                1,000+
              </div>
              <div className="mt-2 text-sm font-light text-neutral-600">
                FCC dockets indexed and watchable from day one
              </div>
            </div>
            <div>
              <div className="font-display text-4xl font-normal text-cg-ink sm:text-[2.75rem]">
                Page-cited
              </div>
              <div className="mt-2 text-sm font-light text-neutral-600">
                Every assertion links to a page in its source filing — so any claim is one click to verify
              </div>
            </div>
          </div>
        </section>

        {/* Pain → Solution side-by-side. Consolidates the prior Comparison +
            Capabilities sections into one before/after that's faster to scan. */}
        <section className="border-t border-cg-border bg-cg-cream px-4 py-20 sm:px-6">
          <div className="mx-auto max-w-7xl">
            <div className="mx-auto mb-14 flex max-w-2xl flex-col items-stretch sm:items-center">
              <div className="mb-6 flex w-full max-w-md items-center gap-3.5 sm:mx-auto">
                <span className="whitespace-nowrap text-[11px] font-medium uppercase tracking-[0.07em] text-neutral-400">
                  Why CommsGenie
                </span>
                <div className="h-px flex-1 bg-neutral-300" />
              </div>
              <h2 className="text-center font-display text-3xl font-normal text-cg-ink sm:text-4xl">
                {copy.painHeading}
              </h2>
              <p className="mt-3 text-center text-lg font-light text-neutral-600">
                The work that takes the rest of the day today, done before lunch.
              </p>
            </div>

            <div className="mx-auto grid max-w-6xl grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16">
              {/* Today, by hand */}
              <div>
                <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-rose-700">
                  <Clock className="h-3.5 w-3.5" />
                  Today, by hand
                </div>
                <h3 className="font-display text-2xl font-normal text-cg-ink">
                  Two days you'll never get back.
                </h3>
                <ol className="mt-6 space-y-5">
                  {[
                    'Open ECFS, bulk-download one PDF at a time — sometimes 200+ for a big proceeding.',
                    'Read every comment, skipping form letters yourself. 80–90% of what you read is noise.',
                    'Extract each filer\'s position into a Word doc, manually building a stakeholder matrix.',
                    'Re-check the docket Monday morning to catch any Friday-evening reply filings you missed.',
                    copy.painLastStep,
                  ].map((step, i) => (
                    <li key={i} className="flex gap-3 text-[15px] leading-relaxed text-neutral-700">
                      <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-rose-200 bg-white text-xs font-semibold text-rose-700">
                        {i + 1}
                      </span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
              </div>

              {/* With CommsGenie */}
              <div>
                <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-cg-green/30 bg-cg-green/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-cg-green">
                  <Sparkles className="h-3.5 w-3.5" />
                  With CommsGenie
                </div>
                <h3 className="font-display text-2xl font-normal text-cg-ink">
                  The same memo, in three minutes.
                </h3>
                <ol className="mt-6 space-y-5">
                  {[
                    'Paste the docket number — the full record is already indexed and watchable.',
                    'Pick topics; defaults handle the rest. Form letters and bot filings filtered automatically.',
                    'Positions summarized by filer with every assertion page-cited to source. Export as .docx.',
                    'Watchlist alerts you the moment a stakeholder files — including Friday at 5pm.',
                    copy.solutionLastStep,
                  ].map((step, i) => (
                    <li key={i} className="flex gap-3 text-[15px] leading-relaxed text-neutral-700">
                      <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-cg-green/30 bg-white text-xs font-semibold text-cg-green">
                        {i + 1}
                      </span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </section>

        {/* Output Proof — the missing "this is what it actually looks like" moment.
            Currently uses a mockup screenshot rendered from real Docket 26-54 content;
            swap for a real /summaries/:n capture when one is available. */}
        <section className="border-t border-cg-border bg-white px-4 py-20 sm:px-6">
          <div className="mx-auto max-w-6xl">
            <div className="mx-auto mb-12 flex max-w-2xl flex-col items-stretch sm:items-center">
              <div className="mb-6 flex w-full max-w-md items-center gap-3.5 sm:mx-auto">
                <span className="whitespace-nowrap text-[11px] font-medium uppercase tracking-[0.07em] text-neutral-400">
                  The output
                </span>
                <div className="h-px flex-1 bg-neutral-300" />
              </div>
              <h2 className="text-center font-display text-3xl font-normal text-cg-ink sm:text-4xl">
                Here's what lands in your inbox.
              </h2>
              <p className="mt-3 text-center text-lg font-light text-neutral-600">
                Page-cited quotes, stakeholder positions grouped by topic, every claim traceable
                to its source filing.
              </p>
            </div>

            {/* Card-fan layout: three product surfaces side-by-side with slight rotation
                so all three read at a glance. Stacks vertically on narrow screens
                because a fan compresses unreadably small below ~700px. */}
            <div className="relative mx-auto max-w-6xl">
              <div
                aria-hidden
                className="absolute -inset-6 -z-0 rounded-[32px] bg-gradient-to-br from-cg-green/15 to-cg-cream blur-2xl"
              />

              {/* Fan layout — only at lg and above. The middle image sits on top, the
                  outer two fan behind it. */}
              <div className="relative hidden lg:block" style={{ height: '560px' }}>
                <img
                  src="/summary-detail.png"
                  alt="CommsGenie per-topic, per-filer breakdown"
                  className="absolute left-0 top-10 w-[46%] origin-bottom-right -rotate-6 rounded-2xl border border-cg-border bg-white shadow-xl transition-transform hover:-translate-y-1 hover:rotate-[-3deg]"
                  loading="lazy"
                />
                <img
                  src="/summary-docx.png"
                  alt="CommsGenie summary exported as .docx"
                  className="absolute right-0 top-10 w-[46%] origin-bottom-left rotate-6 rounded-2xl border border-cg-border bg-white shadow-xl transition-transform hover:-translate-y-1 hover:rotate-[3deg]"
                  loading="lazy"
                />
                <img
                  src="/summary-sample.png"
                  alt="Sample CommsGenie summary output for WC Docket 26-54"
                  className="relative z-10 mx-auto block w-[55%] rounded-2xl border border-cg-border bg-white shadow-2xl"
                  loading="lazy"
                />
              </div>

              {/* Vertical stack at smaller sizes — same three images, no fan. */}
              <div className="relative space-y-6 lg:hidden">
                <img
                  src="/summary-sample.png"
                  alt="Sample CommsGenie summary output for WC Docket 26-54"
                  className="w-full rounded-2xl border border-cg-border bg-white shadow-xl"
                  loading="lazy"
                />
                <img
                  src="/summary-detail.png"
                  alt="CommsGenie per-topic, per-filer breakdown"
                  className="w-full rounded-2xl border border-cg-border bg-white shadow-xl"
                  loading="lazy"
                />
                <img
                  src="/summary-docx.png"
                  alt="CommsGenie summary exported as .docx"
                  className="w-full rounded-2xl border border-cg-border bg-white shadow-xl"
                  loading="lazy"
                />
              </div>
            </div>

            <div className="mx-auto mt-6 grid max-w-3xl grid-cols-1 gap-3 text-center text-xs text-neutral-500 sm:grid-cols-3">
              <p>Summary view — executive summary and topic list</p>
              <p>Per-topic breakdown — every filer's position, page-cited</p>
              <p>{copy.outputDocxCaption}</p>
            </div>
          </div>
        </section>

        {/* How It Works Section — slim three-step rhythm with text-only tab toggle.
            The stat band above already does the time-savings claim; this section's job
            is just to show the sequence of clicks, nothing more. */}
        <section className="border-t border-cg-border bg-cg-cream px-4 py-20 sm:px-6">
          <div className="mx-auto max-w-6xl">
            <div className="mx-auto mb-10 flex max-w-2xl flex-col items-stretch sm:items-center">
              <div className="mb-6 flex w-full max-w-md items-center gap-3.5 sm:mx-auto">
                <span className="whitespace-nowrap text-[11px] font-medium uppercase tracking-[0.07em] text-neutral-400">
                  Workflows
                </span>
                <div className="h-px flex-1 bg-neutral-300" />
              </div>
              <h2 className="text-center font-display text-3xl font-normal text-cg-ink sm:text-4xl">
                How teams use CommsGenie
              </h2>
              <p className="mt-3 text-center text-base font-light text-neutral-600">
                Two workflows. Three steps each.
              </p>
            </div>

            {/* Text-only tab toggle with underline indicator. Was three big pill buttons. */}
            <div className="mb-10 flex justify-center gap-8 border-b border-cg-border">
              {[
                { id: 'commentPeriods' as const, label: 'Analyze comment periods' },
                { id: 'watchlist' as const, label: 'Monitor active dockets' },
              ].map((tab) => {
                const isActive = activeWorkflow === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveWorkflow(tab.id)}
                    aria-pressed={isActive}
                    className={`-mb-px border-b-2 px-1 py-3 text-sm font-medium transition-colors sm:text-base ${
                      isActive
                        ? 'border-cg-green text-cg-ink'
                        : 'border-transparent text-neutral-500 hover:text-neutral-800'
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Steps + a workflow-specific product mock. The two tabs previously
                differed only in text; each now pairs its three steps with a visual
                of what that workflow produces — a page-cited summary card for comment
                analysis, a live alert feed for docket monitoring. */}
            {(() => {
              const isComment = activeWorkflow === 'commentPeriods';
              const steps = isComment
                ? [
                    { icon: Search, title: 'Pick a docket.', body: 'Search 1,000+ indexed FCC proceedings, or paste a docket number.' },
                    { icon: Sparkles, title: 'Generate the summary.', body: 'Spam filtered, positions grouped by filer, every assertion page-cited.' },
                    { icon: Download, title: 'Review and share.', body: 'Download the .docx, or post to a team workspace in one click.' },
                  ]
                : [
                    { icon: Bookmark, title: 'Follow a docket.', body: 'Click Follow on any of the 1,000+ active FCC proceedings.' },
                    { icon: BellRing, title: 'Get alerted.', body: 'Real-time email the moment a filing posts — or daily/weekly digests.' },
                    { icon: Sparkles, title: "Summarize what's new.", body: 'Jump to the new filings from your Library and generate a fresh summary.' },
                  ];
              return (
                <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
                  {/* Steps */}
                  <ol className="space-y-6">
                    {steps.map((step, i) => {
                      const Icon = step.icon;
                      return (
                        <li key={i} className="flex gap-4">
                          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-cg-green/10 text-cg-green">
                            <Icon className="h-5 w-5" strokeWidth={1.75} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-display text-sm font-light text-cg-green/70">
                                {String(i + 1).padStart(2, '0')}
                              </span>
                              <h3 className="text-base font-semibold text-cg-ink sm:text-lg">{step.title}</h3>
                            </div>
                            <p className="mt-1 text-sm leading-relaxed text-neutral-600">{step.body}</p>
                          </div>
                        </li>
                      );
                    })}
                  </ol>

                  {/* Workflow visual */}
                  <div className="relative">
                    <div
                      aria-hidden
                      className="absolute -inset-4 -z-0 rounded-3xl bg-gradient-to-br from-cg-green/15 to-cg-cream blur-2xl"
                    />
                    <div className="relative rounded-2xl border border-cg-border bg-white p-5 shadow-xl">
                      {isComment ? (
                        <>
                          <div className="flex items-center justify-between">
                            <span className="flex items-center gap-2 text-sm font-semibold text-cg-ink">
                              <Sparkles className="h-4 w-4 text-cg-green" /> Comment Summary
                            </span>
                            <span className="rounded-full bg-cg-green/10 px-2 py-0.5 text-[11px] font-semibold text-cg-green">
                              41 filings
                            </span>
                          </div>
                          <p className="mt-1 text-xs text-neutral-500">WC Docket 26-54 · Space Modernization</p>
                          <div className="mt-4 space-y-2.5">
                            {[
                              { topic: 'Intersatellite links', quote: '"…authorize by rule licensed satellite operators to use MSS ISLs for TT&C and data relay."', src: 'Capella Space · p.2' },
                              { topic: 'Spectrum coexistence', quote: '"Coexistence with incumbent WCS licensees requires clearly defined power limits."', src: 'CTIA · p.7' },
                            ].map((q, i) => (
                              <div key={i} className="rounded-lg border border-cg-border bg-cg-cream/40 p-3">
                                <div className="text-xs font-semibold text-cg-ink">{q.topic}</div>
                                <p className="mt-1 text-xs italic leading-relaxed text-neutral-600">{q.quote}</p>
                                <div className="mt-1.5 flex items-center gap-1 text-[11px] text-neutral-400">
                                  <FileText className="h-3 w-3" /> {q.src}
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="mt-4 inline-flex items-center gap-2 rounded-lg bg-cg-nav px-3 py-2 text-xs font-medium text-white">
                            <Download className="h-3.5 w-3.5" /> Export as .docx
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex items-center justify-between">
                            <span className="flex items-center gap-2 text-sm font-semibold text-cg-ink">
                              <BellRing className="h-4 w-4 text-cg-green" /> Watchlist alerts
                            </span>
                            <span className="flex items-center gap-1.5 rounded-full bg-cg-green/10 px-2 py-0.5 text-[11px] font-semibold text-cg-green">
                              <span className="h-1.5 w-1.5 rounded-full bg-cg-green" /> Live
                            </span>
                          </div>
                          <div className="mt-4 space-y-2">
                            {[
                              { d: 'WC 23-108', f: 'AT&T', t: 'Reply comment', ago: '2m ago' },
                              { d: 'WC 26-54', f: 'Capella Space', t: 'Comment', ago: '1h ago' },
                              { d: 'WC 22-2', f: 'CTIA', t: 'Ex parte notice', ago: '3h ago' },
                            ].map((a, i) => (
                              <div key={i} className="flex items-start gap-2.5 rounded-lg border border-cg-border bg-white p-2.5">
                                <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-cg-green" />
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="text-xs font-semibold text-cg-ink">New filing · {a.d}</span>
                                    <span className="shrink-0 text-[11px] text-neutral-400">{a.ago}</span>
                                  </div>
                                  <div className="text-xs text-neutral-600">{a.f} · {a.t}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="mt-4 flex items-center gap-2 rounded-lg bg-cg-green/10 px-3 py-2 text-xs font-medium text-cg-green">
                            <Sparkles className="h-3.5 w-3.5" /> Daily digest ready — 3 new filings
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </section>

        {/* FAQ Section */}
        <section className="border-t border-cg-border bg-white px-4 py-20 sm:px-6">
          <div className="mx-auto max-w-4xl">
            <div className="mb-12 text-center">
              <h2 className="font-display text-3xl font-normal text-cg-ink sm:text-4xl">
                Frequently asked questions
              </h2>
            </div>
            <div className="space-y-4">
              <FAQItem
                question="How much does it cost?"
                answer={
                  <>
                    <p>
                      CommsGenie is free to start--your first 5 AI comment summaries each month are included, no credit
                      card required.
                    </p>
                    <p>
                      Pro is $199/month for unlimited summaries. For practices with multiple attorneys, our Firm plan
                      covers your entire team at a flat monthly rate. See the pricing page for full details.
                    </p>
                  </>
                }
                defaultOpen={false}
              />

              <FAQItem
                question="What counts as a summary?"
                answer={
                  <>
                    <p>
                      A summary is generated when you ask CommsGenie to analyze the comment record for an FCC
                      proceeding. This involves ingesting and processing potentially hundreds of individual filings to
                      produce a structured, page-cited analysis you can verify quickly and shape into a filing-ready
                      work product.
                    </p>
                    <p>
                      Docket search, watchlist alerts, and bulk filing download do not count against your summary
                      allowance--those features are always free.
                    </p>
                  </>
                }
                defaultOpen={false}
              />

              <FAQItem
                question="What happens after my 5 monthly summaries?"
                answer={
                  <p>
                    Once you reach your monthly limit, you can upgrade to Pro for unlimited access at $199/month,
                    or wait for your allowance to reset at the start of your next billing period. All other features--
                    docket tracking, watchlist alerts, bulk download, and workspaces--remain fully available.
                  </p>
                }
                defaultOpen={false}
              />

              <FAQItem
                question="What makes CommsGenie different from ChatGPT or other AI tools?"
                answer={
                  <>
                    <p>
                      The natural ChatGPT approach is to paste a docket's filings into the chat
                      and ask for a summary. That works on small dockets and breaks down on real
                      ones — for three reasons that are structural, not solvable with a better
                      prompt:
                    </p>
                    <ul className="list-disc pl-5 space-y-2">
                      <li>
                        <strong>Non-determinism.</strong> The same comments produce a
                        different-shaped output every chat — different headings, different
                        ordering, different things called important. You can't build a
                        repeatable practice on top of that.
                      </li>
                      <li>
                        <strong>Context limits.</strong> Past roughly ten documents, an LLM's
                        ability to track every filer's position across the full corpus degrades
                        sharply. Important stakeholders get flattened into a generic paragraph
                        or dropped entirely, and you have no way to tell which.
                      </li>
                      <li>
                        <strong>No professional output shape.</strong> Chat returns prose. A
                        regulatory memo needs structured sections, page citations, and a format
                        your team — and your client — recognizes on sight.
                      </li>
                    </ul>
                    <p>
                      CommsGenie is built as a professional workflow, not a chat prompt. Each
                      filing is processed individually with structured extraction (positions,
                      supporting evidence, page numbers), then synthesized across the full
                      record. The output format is constrained by a schema so the same docket
                      produces the same shape every time, and the pipeline is built to keep every
                      filer accounted for on dockets with hundreds of filings — rather than
                      flattening them into a generic paragraph. Every assertion in the output is
                      traceable to its source filing on a specific page, so your review is a
                      spot-check rather than a re-read.
                    </p>
                  </>
                }
              />

              <FAQItem
                question="How accurate are the AI summaries?"
                answer={
                  <>
                    <p>
                      Our summaries are designed to be a strong, verifiable first draft —
                      structured, page-cited, and fast to check — rather than a finished work
                      product you file unread. Every assertion is paired with a page citation back
                      to its source filing, so verifying a claim is a quick spot-check rather than a re-read.
                    </p>
                    <p>
                      As with any research tool, we recommend attorney review for critical matters. Each summary includes
                      structured sections by topic and stakeholder position so verification is straightforward.
                    </p>
                  </>
                }
              />

              <FAQItem
                question="Can I use CommsGenie for proceedings outside of FCC?"
                answer={
                  <p>
                    Not today. CommsGenie's docket ingestion, comment summarization, and watchlist
                    alerts are all built specifically for the FCC's ECFS system. If you'd like to
                    see another regulatory body supported, tell us — it shapes our roadmap.
                  </p>
                }
              />

              <FAQItem
                question="How do you handle data privacy and confidentiality?"
                answer={
                  <>
                    <p>
                      We process documents securely with encryption in transit and at rest. Your documents and data are
                      never used to train AI models.
                    </p>
                    <p>
                      For AI-powered features (summarization, analysis), document content is sent to third-party AI
                      providers (OpenAI, Anthropic) for processing. These providers process your data according to their
                      enterprise/API terms, which prohibit using API data for model training.
                    </p>
                    <p>
                      All document content and generated summaries are stored securely in our database and are only
                      accessible to you and authorized users in your workspace.
                    </p>
                    <p>
                      For complete details, see our{' '}
                      <Link to="/privacy" className="text-cg-green underline transition-colors hover:text-cg-nav">
                        Privacy Policy
                      </Link>
                      .
                    </p>
                  </>
                }
              />

              <FAQItem
                question="How do you guard against hallucinated citations?"
                answer={
                  <>
                    <p>
                      Every quote in a summary is page-grounded — meaning the underlying citation
                      points to the exact filing and page where the language appeared in the
                      original PDF. Topic sections are organized so each filer's position is its own
                      block, which makes spot-checking against source filings fast.
                    </p>
                    <p>
                      We also filter out form letters and bot-generated comments before AI
                      processing, which is the single biggest source of confusion in raw ECFS
                      records.
                    </p>
                    <p>
                      That said, AI is not infallible. We recommend attorney review for any
                      summary that will be filed, served, or sent to a client.
                    </p>
                  </>
                }
              />

              <FAQItem
                question="Will using CommsGenie affect attorney work product or privilege?"
                answer={
                  <>
                    <p>
                      The FCC docket record is public — comments, NPRMs, orders, and public notices
                      that CommsGenie analyzes are not privileged material. Generating a summary on
                      a public docket creates no privilege concern in itself.
                    </p>
                    <p>
                      We do not currently support uploading privileged client materials. If you have
                      a use case for confidential document analysis, contact us directly so we can
                      discuss enterprise-tier handling and BAA-style terms.
                    </p>
                  </>
                }
              />

              <FAQItem
                question="Which AI models do you use, and is my data used to train them?"
                answer={
                  <>
                    <p>
                      CommsGenie uses commercial models from OpenAI and Anthropic via their
                      enterprise/API endpoints. Both providers' API terms prohibit training on
                      customer data, and that prohibition flows through to your use of CommsGenie.
                    </p>
                    <p>
                      Your documents and generated summaries are stored in our database, are not
                      used to train any model (ours or theirs), and are only accessible to you and
                      members of any workspace you share them with.
                    </p>
                  </>
                }
              />

              <FAQItem
                question="Is CommsGenie suitable for firm-wide deployment?"
                answer={
                  <p>
                    Yes — Firm plans cover an entire practice group with shared workspaces,
                    team-wide watchlists, and per-attorney summary allowances. We're early enough
                    that pilots include direct founder access for onboarding and feedback. Email{' '}
                    <a href="mailto:support@commsgenie.com" className="text-cg-green underline hover:text-cg-nav">
                      support@commsgenie.com
                    </a>{' '}
                    to discuss a practice-group pilot.
                  </p>
                }
              />

              <FAQItem
                question="Can I try it before committing?"
                answer={
                  <p>
                    Yes—your first 5 document summaries are free with no credit card required. This lets you evaluate
                    CommsGenie on your actual FCC work before deciding to continue.
                  </p>
                }
              />
            </div>

            {/* Inline final CTA — replaces the previous full-width dark CTA section.
                Output proof above already shows the destination; one button to act here
                is enough without re-arguing the value. */}
            <div className="mt-14 flex flex-col items-stretch justify-center gap-3 border-t border-cg-border pt-10 sm:flex-row sm:items-center">
              <button
                type="button"
                onClick={() => onSignIn?.()}
                className="group flex w-full items-center justify-center gap-2 rounded-xl bg-cg-nav px-8 py-4 text-lg font-medium text-white shadow-md transition-all duration-200 hover:bg-cg-green hover:shadow-lg sm:w-auto"
              >
                <span>Sign Up Free</span>
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5" />
              </button>
              <button
                type="button"
                onClick={() => navigate('/proceedings')}
                className="w-full rounded-xl border border-cg-border bg-white px-8 py-4 text-lg font-medium text-cg-ink shadow-sm transition-all duration-200 hover:border-neutral-400 hover:bg-neutral-50 sm:w-auto"
              >
                Browse dockets first
              </button>
            </div>
            <p className="mt-4 text-center text-sm font-light text-neutral-500">
              5 AI summaries/month free, no credit card required
            </p>
          </div>
        </section>
      </div>
    );
  }

  const runDocketSearch = () => {
    const q = docketSearchInput.trim();
    if (!q) return;
    navigate(`/proceedings?q=${encodeURIComponent(q)}`);
  };

  const exampleChipClass =
    'mx-0.5 rounded-md border border-neutral-300 bg-neutral-200/80 px-2 py-1 text-sm text-neutral-600 cursor-pointer hover:bg-neutral-300/80 transition-colors';

  // Logged-in dashboard — use most of app width; larger type
  return (
    <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col lg:max-w-7xl">
      <section className="px-0 pb-12 pt-12 sm:pt-16">
        <p className="mb-4 text-xs font-medium uppercase tracking-[0.1em] text-cg-green sm:text-sm">
          FCC Regulatory Intelligence
        </p>
        <h1 className="font-display text-[clamp(2.375rem,4vw+1rem,3.5rem)] font-normal leading-[1.08] tracking-tight text-cg-ink">
          Stay ahead of the<br />
          <span className="italic !text-[var(--cg-green)]">docket.</span>
        </h1>
        <p className="mt-5 max-w-3xl text-base font-light leading-relaxed text-neutral-600 sm:text-lg">
          {dashboardCopy.dashboardSubhead}
        </p>

        <form
          className="mt-10 w-full max-w-3xl lg:max-w-4xl"
          onSubmit={(e) => {
            e.preventDefault();
            runDocketSearch();
          }}
        >
          <div ref={docketSearchContainerRef} className="relative">
            <div className="flex cursor-text items-center gap-3 rounded-[10px] border border-cg-border bg-white px-4 py-3 shadow-sm transition-shadow focus-within:border-cg-green focus-within:shadow-[0_0_0_3px_rgba(46,158,101,0.12)] hover:border-neutral-400 sm:px-5 sm:py-3.5">
              <Search className="h-5 w-5 shrink-0 text-neutral-400" aria-hidden />
              <input
                type="search"
                value={docketSearchInput}
                onChange={(e) => {
                  const v = e.target.value;
                  setDocketSearchInput(v);
                  docketSearchSetQuery(v);
                }}
                onKeyDown={(e) => {
                  const picked = docketSearchHandleKeyDown(e);
                  if (picked) {
                    setDocketSearchInput(picked);
                    navigate(`/proceedings/${encodeURIComponent(picked)}`);
                  }
                }}
                onFocus={() => {
                  if (docketSearchSuggestions.length > 0) {
                    docketSearchSetShowSuggestions(true);
                  }
                }}
                placeholder="Search by docket number, topic, or proceeding name…"
                className="min-w-0 flex-1 border-0 bg-transparent text-base font-normal text-cg-ink outline-none placeholder:text-neutral-400"
                aria-label="Search proceedings"
                autoComplete="off"
              />
              {docketSearchLoading && (
                <Loader className="h-4 w-4 shrink-0 animate-spin text-neutral-400" />
              )}
              <button
                type="submit"
                className="shrink-0 rounded-md bg-cg-nav px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-cg-green sm:text-base"
              >
                Search
              </button>
            </div>

            {docketSearchShowSuggestions && docketSearchSuggestions.length > 0 && (
              <div className="absolute left-0 right-0 top-full z-30 mt-1 overflow-hidden rounded-xl border border-cg-border bg-white shadow-lg">
                {docketSearchSuggestions.map((s, idx) => (
                  <button
                    key={`${s.docketNumber}-${idx}`}
                    type="button"
                    onClick={() => {
                      const picked = docketSearchSelectSuggestion(idx);
                      if (picked) {
                        setDocketSearchInput(picked);
                        navigate(`/proceedings/${encodeURIComponent(picked)}`);
                      }
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                    className={`flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors ${
                      idx === docketSearchSelectedIndex
                        ? 'bg-cg-green/5 text-cg-ink'
                        : 'text-neutral-700 hover:bg-neutral-50'
                    }`}
                  >
                    <FileText className="h-4 w-4 shrink-0 text-neutral-400" />
                    <div className="min-w-0">
                      <div className="font-medium text-cg-ink">{s.docketNumber}</div>
                      {s.displayText && s.displayText !== s.docketNumber && (
                        <div className="truncate text-xs text-neutral-500">{s.displayText}</div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <p className="mt-3 text-sm font-light text-neutral-400">
            Try{' '}
            <button type="button" className={exampleChipClass} onClick={() => navigate('/proceedings?q=WC%2023-108')}>
              WC 23-108
            </button>{' '}
            or{' '}
            <button
              type="button"
              className={exampleChipClass}
              onClick={() => navigate('/proceedings?q=Net%20Neutrality')}
            >
              Net Neutrality
            </button>{' '}
            or{' '}
            <button
              type="button"
              className={exampleChipClass}
              onClick={() => navigate('/proceedings?q=Broadband%20Labels')}
            >
              Broadband Labels
            </button>
          </p>
        </form>
      </section>

      {showWelcomeBanner && (
        <div className="relative mt-8 rounded-xl border border-cg-border bg-white p-5 pr-11 sm:p-6">
          <button
            type="button"
            onClick={dismissWelcomeBanner}
            className="absolute right-3 top-3 rounded-lg p-1.5 text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-800"
            aria-label="Dismiss welcome banner"
          >
            <X className="h-5 w-5" />
          </button>
          <p className="text-base font-medium text-cg-ink">First time here?</p>
          <p className="mt-1 text-base font-light text-neutral-600">
            Use Explore below, or search above to jump straight into a docket.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => {
                navigate('/proceedings');
                dismissWelcomeBanner();
              }}
              className="rounded-md bg-cg-nav px-5 py-2.5 text-base font-medium text-white transition-colors hover:bg-cg-green"
            >
              Find a docket
            </button>
            <button
              type="button"
              onClick={() => {
                navigate('/trending');
                dismissWelcomeBanner();
              }}
              className="rounded-md border border-cg-border bg-white px-5 py-2.5 text-base font-medium text-cg-ink transition-colors hover:bg-neutral-50"
            >
              Trending
            </button>
          </div>
        </div>
      )}

      <div className="mt-12 px-0 pb-16">
        <div className="mb-6 flex items-center gap-3.5">
          <span className="whitespace-nowrap text-xs font-medium uppercase tracking-[0.07em] text-neutral-400 sm:text-sm">
            Explore
          </span>
          <div className="h-px flex-1 bg-neutral-300" />
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5">
          {menuOptions.map((option) => {
            const IconComponent = option.icon;
            const isPremiumFeature = option.isPremium;
            const hasAccess = !isPremiumFeature || userTier === 'pro' || userTier === 'enterprise';
            const stripeGreen = option.accent === 'green';

            const handleClick = () => {
              if (isPremiumFeature && !hasAccess) {
                navigate('/pricing');
                return;
              }
              navigate(option.path);
            };

            return (
              <button
                key={option.id}
                type="button"
                onClick={handleClick}
                className={`group relative overflow-hidden rounded-xl border border-cg-border bg-white p-7 text-left transition-all hover:-translate-y-px hover:border-neutral-400 hover:shadow-md sm:p-8 ${
                  !hasAccess ? 'opacity-80' : ''
                }`}
              >
                <div
                  className={`absolute left-0 right-0 top-0 h-[3px] rounded-t-xl ${
                    stripeGreen ? 'bg-cg-green' : 'bg-cg-amber'
                  }`}
                />
                <div
                  className={`mb-4 flex h-11 w-11 items-center justify-center rounded-lg ${
                    stripeGreen ? 'bg-[#eaf6f0] text-[#1a7a4b]' : 'bg-[#fef3e2] text-[#b45309]'
                  }`}
                >
                  <IconComponent className="h-5 w-5" strokeWidth={1.75} />
                </div>
                <div className="flex items-start justify-between gap-3 pr-10">
                  <div>
                    <h2 className="text-base font-medium tracking-tight text-cg-ink sm:text-lg">{option.title}</h2>
                    {isPremiumFeature && !hasAccess && (
                      <span className="mt-1 inline-block rounded-full border border-orange-200 bg-orange-50 px-2 py-0.5 text-xs font-semibold text-orange-700">
                        Pro
                      </span>
                    )}
                    <p className="mt-2 text-sm font-light leading-relaxed text-neutral-600 sm:text-base">
                      {option.description}
                    </p>
                    {isPremiumFeature && !hasAccess && (
                      <p className="mt-2 text-sm font-semibold text-orange-600">Upgrade to access</p>
                    )}
                  </div>
                </div>
                <div className="absolute bottom-6 right-6 flex h-9 w-9 items-center justify-center rounded-full bg-[#f3f3ef] text-base text-neutral-500 transition-colors group-hover:bg-cg-nav group-hover:text-white">
                  <ArrowRight className="h-4 w-4" />
                </div>
              </button>
            );
          })}
        </div>

        <div className="mb-6 mt-12 flex items-center gap-3.5">
          <span className="whitespace-nowrap text-xs font-medium uppercase tracking-[0.07em] text-neutral-400 sm:text-sm">
            Your workspace
          </span>
          <div className="h-px flex-1 bg-neutral-300" />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
          {bottomMenuOptions.map((option) => {
            const IconComponent = option.icon;
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => navigate(option.path)}
                className="group flex w-full items-center gap-4 rounded-[10px] border border-cg-border bg-white px-5 py-4 text-left transition-colors hover:border-neutral-400 hover:bg-[#fafaf8] sm:px-6 sm:py-5"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-[#f0f0ec] text-neutral-600">
                  <IconComponent className="h-5 w-5" strokeWidth={1.75} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-base font-medium text-cg-ink sm:text-lg">{option.title}</div>
                  <div className="mt-1 text-sm font-light text-neutral-500 sm:text-base">{option.description}</div>
                </div>
                <span className="shrink-0 text-base text-neutral-300 transition-colors group-hover:text-neutral-500">
                  →
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};